import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Grid,
  Card,
  Button,
  Input,
  Segment,
  Header,
  Icon,
  Pagination,
  Label,
} from 'semantic-ui-react';
import { recipes } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './Recipes.css';

function Recipes() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data: recipesList, isLoading } = useQuery({
    queryKey: ['recipes', search, page],
    queryFn: () => recipes.list({ search, page }),
    keepPreviousData: true,
  });

  const handlePageChange = (_e, { activePage }) => {
    setPage(Number(activePage));
  };

  const handleCreateRecipe = () => {
    navigate('/calculator');
  };

  return (
    <>
      <Helmet>
        <title>Miakara Soap Recipes - Recipes</title>
      </Helmet>

      <Container>
        <Segment vertical>
          <Grid>
            <Grid.Row>
              <Grid.Column width={16}>
                <Header as="h1">Recipes</Header>
                <Input
                  icon="search"
                  placeholder="Search recipes..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1); // reset to first page on search
                  }}
                  fluid
                />
              </Grid.Column>
            </Grid.Row>

            {user && (
              <Grid.Row>
                <Grid.Column width={16}>
                  <Button primary onClick={handleCreateRecipe}>
                    <Icon name="plus" /> Create Recipe
                  </Button>
                </Grid.Column>
              </Grid.Row>
            )}

            <Grid.Row>
              <Grid.Column width={16}>
                {isLoading ? (
                  <Segment loading />
                ) : recipesList?.data?.data?.length > 0 ? (
                  <>
                    <Card.Group>
                      {recipesList.data.data.map((recipe) => (
                        <Card key={recipe.id}>
                          <Card.Content>
                            <Card.Header>
                              {recipe.name}
                              {!recipe.visibility && (
                                <Label color="grey" size="tiny" style={{ marginLeft: '0.5em' }}>
                                  Private
                                </Label>
                              )}
                            </Card.Header>
                            <Card.Meta>
                              <span>By {recipe.user?.name || 'Anonymous'}</span>
                              <span style={{ marginLeft: '1em' }}>
                                {new Date(recipe.created_at).toLocaleDateString()}
                              </span>
                            </Card.Meta>
                            <Card.Description>
                              {recipe.description ? (
                                <div 
                                  className="recipe-description"
                                  dangerouslySetInnerHTML={{ __html: recipe.description }}
                                />
                              ) : (
                                <em>No description provided</em>
                              )}
                            </Card.Description>
                          </Card.Content>
                          <Card.Content extra>
                            <Grid columns={2}>
                              <Grid.Column>
                                <div>
                                  <Icon name="lab" />
                                  {recipe.oils?.length || 0} oils
                                </div>
                              </Grid.Column>
                              <Grid.Column textAlign="right">
                                <Button 
                                  primary 
                                  basic 
                                  size="small"
                                  as={Link}
                                  to={`/recipes/${recipe.id}`}
                                >
                                  View Recipe
                                </Button>
                              </Grid.Column>
                            </Grid>
                          </Card.Content>
                        </Card>
                      ))}
                    </Card.Group>
                    {recipesList.data.last_page > 1 && (
                      <Segment basic textAlign="center">
                        <Pagination
                          activePage={page}
                          totalPages={recipesList.data.last_page}
                          onPageChange={handlePageChange}
                          secondary
                          pointing
                        />
                      </Segment>
                    )}
                  </>
                ) : (
                  <Segment placeholder>
                    <Header icon>
                      <Icon name="food" />
                      {search ? 'No recipes found matching your search.' : 'No recipes found.'}
                      {!user && (
                        <Header.Subheader>
                          <Link to="/auth">Sign in</Link> to create your own recipes.
                        </Header.Subheader>
                      )}
                    </Header>
                  </Segment>
                )}
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Segment>
      </Container>
    </>
  );
}

export default Recipes;
